/**
 * Pure helpers for a drawn search area (GeoJSON Polygon, one outer ring, coordinates [lng, lat]).
 * Used for acquisition lists: "Bereich auf der Karte zeichnen" instead of a radius.
 * Copy of shrd/utils/helpers/geoPolygon.helper.ts (feat/acq-area-polygon), keep both in sync.
 */

export type LngLat = [number, number];

export interface GeoJsonPolygon {
    type: 'Polygon';
    coordinates: LngLat[][];
}

/** Distinct corner points of a drawn area */
export const AREA_MIN_POINTS = 3;
export const AREA_MAX_POINTS = 100;

export enum AreaPolygonError {
    NOT_A_POLYGON = 'NOT_A_POLYGON',
    HOLES_NOT_SUPPORTED = 'HOLES_NOT_SUPPORTED',
    INVALID_COORDINATE = 'INVALID_COORDINATE',
    NOT_CLOSED = 'NOT_CLOSED',
    TOO_FEW_POINTS = 'TOO_FEW_POINTS',
    TOO_MANY_POINTS = 'TOO_MANY_POINTS',
    DEGENERATE = 'DEGENERATE',
    SELF_INTERSECTING = 'SELF_INTERSECTING',
}

const EPSILON = 1e-12;

function isCoordinate(value: unknown): value is LngLat {
    return Array.isArray(value) && value.length === 2
        && typeof value[0] === 'number' && typeof value[1] === 'number'
        && Number.isFinite(value[0]) && Number.isFinite(value[1])
        && value[0] >= -180 && value[0] <= 180 && value[1] >= -90 && value[1] <= 90;
}

function samePoint(a: LngLat, b: LngLat): boolean {
    return a[0] === b[0] && a[1] === b[1];
}

/** Corner points without the closing point and without consecutive duplicates */
export function openRing(ring: LngLat[]): LngLat[] {
    const result: LngLat[] = [];
    for (const point of ring) {
        if (!result.length || !samePoint(result[result.length - 1], point)) {
            result.push([point[0], point[1]]);
        }
    }
    while (result.length > 1 && samePoint(result[0], result[result.length - 1])) {
        result.pop();
    }
    return result;
}

/** Closed ring (first point repeated at the end), as GeoJSON and MongoDB require it */
export function closeRing(points: LngLat[]): LngLat[] {
    const open = openRing(points);
    return open.length ? [...open, [open[0][0], open[0][1]]] : [];
}

/** GeoJSON polygon from the drawn corner points (open or closed) */
export function toAreaPolygon(points: LngLat[]): GeoJsonPolygon {
    return {type: 'Polygon', coordinates: [closeRing(points)]};
}

function orientation(a: LngLat, b: LngLat, c: LngLat): number {
    const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]);
    if (Math.abs(value) < EPSILON) {
        return 0;
    }
    return value > 0 ? 1 : 2;
}

function onSegment(a: LngLat, b: LngLat, c: LngLat): boolean {
    return b[0] <= Math.max(a[0], c[0]) && b[0] >= Math.min(a[0], c[0]) && b[1] <= Math.max(a[1], c[1]) && b[1] >= Math.min(a[1], c[1]);
}

/** Whether segment p1-q1 intersects or touches segment p2-q2 */
export function segmentsIntersect(p1: LngLat, q1: LngLat, p2: LngLat, q2: LngLat): boolean {
    const o1 = orientation(p1, q1, p2);
    const o2 = orientation(p1, q1, q2);
    const o3 = orientation(p2, q2, p1);
    const o4 = orientation(p2, q2, q1);
    if (o1 !== o2 && o3 !== o4) {
        return true;
    }
    return (o1 === 0 && onSegment(p1, p2, q1)) || (o2 === 0 && onSegment(p1, q2, q1))
        || (o3 === 0 && onSegment(p2, p1, q2)) || (o4 === 0 && onSegment(p2, q1, q2));
}

/** Whether two non-adjacent edges of the (implicitly closed) ring cross or touch; also catches repeated corner points */
export function isSelfIntersecting(points: LngLat[]): boolean {
    const open = openRing(points);
    const n = open.length;
    if (n < 4) {
        // A triangle cannot cross itself; repeated points are caught as DEGENERATE
        return false;
    }
    for (let i = 0; i < n; i++) {
        const a1 = open[i];
        const a2 = open[(i + 1) % n];
        for (let j = i + 1; j < n; j++) {
            const adjacent = j === i + 1 || (i === 0 && j === n - 1);
            if (adjacent) {
                continue;
            }
            if (segmentsIntersect(a1, a2, open[j], open[(j + 1) % n])) {
                return true;
            }
        }
    }
    return false;
}

/** Planar signed area in degrees² (shoelace); only used to detect a flat area */
export function planarArea(points: LngLat[]): number {
    const open = openRing(points);
    let sum = 0;
    for (let i = 0; i < open.length; i++) {
        const [x1, y1] = open[i];
        const [x2, y2] = open[(i + 1) % open.length];
        sum += x1 * y2 - x2 * y1;
    }
    return sum / 2;
}

/** Validates corner points of a drawn area (without the GeoJSON wrapper); null = valid */
export function validateAreaPoints(points: LngLat[]): AreaPolygonError | null {
    if (!Array.isArray(points) || !points.every(isCoordinate)) {
        return AreaPolygonError.INVALID_COORDINATE;
    }
    const open = openRing(points);
    const distinct = new Set(open.map((el) => `${el[0]},${el[1]}`)).size;
    if (open.length < AREA_MIN_POINTS || distinct < AREA_MIN_POINTS) {
        return AreaPolygonError.TOO_FEW_POINTS;
    }
    if (open.length > AREA_MAX_POINTS) {
        return AreaPolygonError.TOO_MANY_POINTS;
    }
    if (distinct !== open.length) {
        return AreaPolygonError.DEGENERATE;
    }
    if (isSelfIntersecting(open)) {
        return AreaPolygonError.SELF_INTERSECTING;
    }
    // All points on one line (a bow tie also has ~0 signed area, but is reported as crossing above)
    if (Math.abs(planarArea(open)) < 1e-10) {
        return AreaPolygonError.DEGENERATE;
    }
    return null;
}

/** Validates a stored / requested GeoJSON polygon; null = valid */
export function validateAreaPolygon(polygon: unknown): AreaPolygonError | null {
    const value = polygon as GeoJsonPolygon;
    if (!value || typeof value !== 'object' || value.type !== 'Polygon' || !Array.isArray(value.coordinates) || !value.coordinates.length) {
        return AreaPolygonError.NOT_A_POLYGON;
    }
    if (value.coordinates.length > 1) {
        return AreaPolygonError.HOLES_NOT_SUPPORTED;
    }
    const ring = value.coordinates[0];
    if (!Array.isArray(ring) || !ring.every(isCoordinate)) {
        return AreaPolygonError.INVALID_COORDINATE;
    }
    if (ring.length < 2 || !samePoint(ring[0], ring[ring.length - 1])) {
        return AreaPolygonError.NOT_CLOSED;
    }
    return validateAreaPoints(ring);
}

export const AREA_POLYGON_ERROR_TEXT: Record<AreaPolygonError, string> = {
    [AreaPolygonError.NOT_A_POLYGON]: 'Der Bereich ist keine Fläche',
    [AreaPolygonError.HOLES_NOT_SUPPORTED]: 'Der Bereich darf keine Löcher haben',
    [AreaPolygonError.INVALID_COORDINATE]: 'Der Bereich enthält ungültige Koordinaten',
    [AreaPolygonError.NOT_CLOSED]: 'Der Bereich ist nicht geschlossen',
    [AreaPolygonError.TOO_FEW_POINTS]: `Der Bereich braucht mindestens ${AREA_MIN_POINTS} Punkte`,
    [AreaPolygonError.TOO_MANY_POINTS]: `Der Bereich darf höchstens ${AREA_MAX_POINTS} Punkte haben`,
    [AreaPolygonError.DEGENERATE]: 'Der Bereich hat keine Fläche (doppelte Punkte oder alle auf einer Linie)',
    [AreaPolygonError.SELF_INTERSECTING]: 'Die Linien des Bereichs kreuzen sich',
};

/** Normalized copy for storing and querying: closed ring, consecutive duplicates removed */
export function normalizeAreaPolygon(polygon: GeoJsonPolygon): GeoJsonPolygon {
    return toAreaPolygon(polygon.coordinates[0]);
}

/**
 * MongoDB condition for "address.location inside the area". Uses the 2dsphere index of address.location
 * (the same one as the radius search with $near). Throws for an invalid polygon.
 */
export function buildWithinAreaCondition(polygon: GeoJsonPolygon, path = 'address.location'): Record<string, unknown> {
    const error = validateAreaPolygon(polygon);
    if (error) {
        throw new Error(`Invalid area polygon: ${error}`);
    }
    return {[path]: {$geoWithin: {$geometry: normalizeAreaPolygon(polygon)}}};
}

/** Ray casting, planar; good enough for the size of a drawn acquisition area */
export function pointInPolygon(point: LngLat, polygon: GeoJsonPolygon | LngLat[]): boolean {
    const ring = openRing(Array.isArray(polygon) ? polygon : polygon.coordinates[0] || []);
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i];
        const [xj, yj] = ring[j];
        const crosses = (yi > point[1]) !== (yj > point[1]) && point[0] < (xj - xi) * (point[1] - yi) / (yj - yi) + xi;
        if (crosses) {
            inside = !inside;
        }
    }
    return inside;
}
